import Vue from 'vue';
import * as showdown from 'showdown';
import AjaxUtils from '../../util/ajax_utils';

/**
 * ApckTrustTooltip - A directive-based tooltip system with markdown support and URL loading
 * 
 * Usage:
 * <span v-tooltip="'Your tooltip text'">Hover over me</span>
 * 
 * Or with markdown:
 * <span v-tooltip="'**Bold** text with *italics*'">Hover me</span>
 * 
 * Or with options:
 * <span v-tooltip="{ text: 'Tooltip', position: 'right', delay: 500, markdown: true }">Hover me</span>
 * 
 * Or as an attribute for position:
 * <span v-tooltip:top="'Tooltip text'">Hover me</span>
 * 
 * Or load from URL with explicit modifier:
 * <span v-tooltip.url="'/api/tooltip/info'">Hover me</span>
 * <span v-tooltip:top.url="'/api/tooltip/info'">Hover me</span>
 * 
 * Or with object and URL:
 * <span v-tooltip="{ url: '/api/tooltip', position: 'right', markdown: true }">Hover me</span>
 * 
 * Positions: top (default), bottom, left, right
 * Modifiers: url (fetch content from URL)
 */

// Create a shared markdown converter instance
const markdownConverter = new showdown.Converter();

const TooltipDirective = {
    bind: function(el, binding) {
        // Parse binding value
        let config = {
            text: '',
            url: null,
            position: binding.arg || 'top',
            delay: 200,
            markdown: false,
            isUrl: binding.modifiers.url || false
        };
        
        if (typeof binding.value === 'string') {
            // Check if .url modifier is used
            if (config.isUrl) {
                config.url = binding.value;
            } else {
                config.text = binding.value;
                // Auto-detect markdown if string contains markdown syntax
                config.markdown = /[*_`#\[\]]/g.test(binding.value);
            }
        } else if (typeof binding.value === 'object') {
            config = Object.assign(config, binding.value);
            
            // If url is provided in object, treat as URL
            if (config.url) {
                config.isUrl = true;
            }
            
            // Auto-detect markdown if not explicitly set and text is provided
            if (config.markdown === false && config.text) {
                config.markdown = /[*_`#\[\]]/g.test(config.text);
            }
        }
        
        if (!config.text && !config.url) return;
        
        // Store config on element
        el._tooltipConfig = config;
        el._tooltipElement = null;
        el._tooltipTimeout = null;
        el._tooltipContentLoaded = false;
        el._tooltipContentCache = null;
        
        // Load content from URL if needed
        function loadTooltipContent(callback) {
            // If content is already loaded, use cache
            if (el._tooltipContentLoaded && el._tooltipContentCache !== null) {
                callback(el._tooltipContentCache);
                return;
            }
            
            // If URL mode, fetch content
            if (config.isUrl && config.url) {
                AjaxUtils.ajax_get({
                    url: config.url,
                    success: function(data) {
                        let content = '';
                        
                        // Handle different response formats
                        if (typeof data === 'string') {
                            content = data;
                        } else if (data && data.content) {
                            content = data.content;
                        } else if (data && data.text) {
                            content = data.text;
                        } else if (data && data.data) {
                            content = data.data;
                        }
                        
                        // Cache the content
                        el._tooltipContentCache = content;
                        el._tooltipContentLoaded = true;
                        
                        // Auto-detect markdown in fetched content if not explicitly set
                        if (config.markdown === false && content) {
                            config.markdown = /[*_`#\[\]]/g.test(content);
                        }
                        
                        callback(content);
                    },
                    error: function(error) {
                        console.error('Failed to load tooltip content from URL:', config.url, error);
                        // Fallback to error message
                        const errorContent = 'Failed to load tooltip content';
                        el._tooltipContentCache = errorContent;
                        el._tooltipContentLoaded = true;
                        callback(errorContent);
                    },
                    opts: {
                        dataType: 'json',
                        flash_form_errors: false,
                        show_success_flash: false,
                        flash_404_errors: false
                    }
                });
            } else {
                // Use text content directly
                callback(config.text);
            }
        }
        
        // Create tooltip element
        function createTooltip(content) {
            const tooltip = document.createElement('div');
            tooltip.className = 'apck-trust-tooltip';
            
            // Show loading state initially if content is being fetched
            if (!content && config.isUrl) {
                // Create loading spinner
                const loadingContainer = document.createElement('div');
                loadingContainer.className = 'tooltip-loading-container';
                loadingContainer.style.display = 'flex';
                loadingContainer.style.alignItems = 'center';
                loadingContainer.style.gap = '8px';
                
                // Create spinner element
                const spinner = document.createElement('div');
                spinner.className = 'tooltip-spinner';
                spinner.style.width = '16px';
                spinner.style.height = '16px';
                spinner.style.border = '2px solid rgba(255, 255, 255, 0.3)';
                spinner.style.borderTopColor = '#ffffff';
                spinner.style.borderRadius = '50%';
                spinner.style.animation = 'tooltip-spin 0.6s linear infinite';
                
                // Add spinner animation styles to document if not already present
                if (!document.getElementById('tooltip-spinner-styles')) {
                    const style = document.createElement('style');
                    style.id = 'tooltip-spinner-styles';
                    style.textContent = `
                        @keyframes tooltip-spin {
                            to { transform: rotate(360deg); }
                        }
                    `;
                    document.head.appendChild(style);
                }
                
                const loadingText = document.createElement('span');
                loadingText.textContent = 'Loading...';
                
                loadingContainer.appendChild(spinner);
                loadingContainer.appendChild(loadingText);
                tooltip.appendChild(loadingContainer);
            } else {
                // Render content based on markdown setting
                if (config.markdown && content) {
                    tooltip.innerHTML = markdownConverter.makeHtml(content);
                } else {
                    tooltip.textContent = content || '';
                }
            }
            
            tooltip.style.position = 'absolute';
            tooltip.style.zIndex = '10000';
            tooltip.style.pointerEvents = 'none';
            tooltip.style.opacity = '0';
            tooltip.style.transition = 'opacity 0.2s ease-in-out';
            
            // Base styling
            tooltip.style.backgroundColor = '#1e293b';
            tooltip.style.color = '#ffffff';
            tooltip.style.padding = '8px 12px';
            tooltip.style.borderRadius = '6px';
            tooltip.style.fontSize = '13px';
            tooltip.style.fontWeight = '500';
            tooltip.style.maxWidth = '400px';
            tooltip.style.wordWrap = 'break-word';
            tooltip.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            
            // Additional styling for markdown content
            if (config.markdown) {
                tooltip.style.lineHeight = '1.5';
                
                // Style markdown elements
                const styleMarkdown = function() {
                    // Paragraphs
                    const paragraphs = tooltip.querySelectorAll('p');
                    paragraphs.forEach(function(p) {
                        p.style.margin = '0 0 8px 0';
                    });
                    if (paragraphs.length > 0) {
                        paragraphs[paragraphs.length - 1].style.marginBottom = '0';
                    }
                    
                    // Strong/bold
                    const strong = tooltip.querySelectorAll('strong');
                    strong.forEach(function(s) {
                        s.style.fontWeight = '700';
                        s.style.color = '#ffffff';
                    });
                    
                    // Em/italic
                    const em = tooltip.querySelectorAll('em');
                    em.forEach(function(e) {
                        e.style.fontStyle = 'italic';
                    });
                    
                    // Code
                    const code = tooltip.querySelectorAll('code');
                    code.forEach(function(c) {
                        c.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        c.style.padding = '2px 4px';
                        c.style.borderRadius = '3px';
                        c.style.fontFamily = 'monospace';
                        c.style.fontSize = '12px';
                    });
                    
                    // Links
                    const links = tooltip.querySelectorAll('a');
                    links.forEach(function(a) {
                        a.style.color = '#60a5fa';
                        a.style.textDecoration = 'underline';
                    });
                    
                    // Lists
                    const lists = tooltip.querySelectorAll('ul, ol');
                    lists.forEach(function(list) {
                        list.style.margin = '4px 0';
                        list.style.paddingLeft = '20px';
                    });
                    
                    const listItems = tooltip.querySelectorAll('li');
                    listItems.forEach(function(li) {
                        li.style.marginBottom = '2px';
                    });
                };
                
                // Apply styles after a short delay to ensure DOM is ready
                setTimeout(styleMarkdown, 0);
            }
            
            document.body.appendChild(tooltip);
            return tooltip;
        }
        
        // Position tooltip
        function positionTooltip(tooltip, targetRect) {
            const tooltipRect = tooltip.getBoundingClientRect();
            const padding = 8;
            let top, left;
            
            // Calculate position based on preference
            switch(config.position) {
                case 'bottom':
                    top = targetRect.bottom + padding;
                    left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
                    break;
                    
                case 'left':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
                    left = targetRect.left - tooltipRect.width - padding;
                    break;
                    
                case 'right':
                    top = targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2);
                    left = targetRect.right + padding;
                    break;
                    
                case 'top':
                default:
                    top = targetRect.top - tooltipRect.height - padding;
                    left = targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2);
                    break;
            }
            
            // Keep tooltip within viewport bounds
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            // Adjust horizontal position
            if (left < padding) {
                left = padding;
            } else if (left + tooltipRect.width > viewportWidth - padding) {
                left = viewportWidth - tooltipRect.width - padding;
            }
            
            // Adjust vertical position
            if (top < padding) {
                top = padding;
            } else if (top + tooltipRect.height > viewportHeight - padding) {
                top = viewportHeight - tooltipRect.height - padding;
            }
            
            tooltip.style.top = top + window.scrollY + 'px';
            tooltip.style.left = left + window.scrollX + 'px';
        }
        
        // Update tooltip content
        function updateTooltipContent(tooltip, content) {
            // Clear any loading spinner
            const loadingContainer = tooltip.querySelector('.tooltip-loading-container');
            if (loadingContainer) {
                tooltip.removeChild(loadingContainer);
            }
            
            // Render content based on markdown setting
            if (config.markdown && content) {
                tooltip.innerHTML = markdownConverter.makeHtml(content);
            } else {
                tooltip.textContent = content || '';
            }
            
            // Apply markdown styling if needed
            if (config.markdown) {
                const styleMarkdown = function() {
                    // Paragraphs
                    const paragraphs = tooltip.querySelectorAll('p');
                    paragraphs.forEach(function(p) {
                        p.style.margin = '0 0 8px 0';
                    });
                    if (paragraphs.length > 0) {
                        paragraphs[paragraphs.length - 1].style.marginBottom = '0';
                    }
                    
                    // Strong/bold
                    const strong = tooltip.querySelectorAll('strong');
                    strong.forEach(function(s) {
                        s.style.fontWeight = '700';
                        s.style.color = '#ffffff';
                    });
                    
                    // Em/italic
                    const em = tooltip.querySelectorAll('em');
                    em.forEach(function(e) {
                        e.style.fontStyle = 'italic';
                    });
                    
                    // Code
                    const code = tooltip.querySelectorAll('code');
                    code.forEach(function(c) {
                        c.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        c.style.padding = '2px 4px';
                        c.style.borderRadius = '3px';
                        c.style.fontFamily = 'monospace';
                        c.style.fontSize = '12px';
                    });
                    
                    // Links
                    const links = tooltip.querySelectorAll('a');
                    links.forEach(function(a) {
                        a.style.color = '#60a5fa';
                        a.style.textDecoration = 'underline';
                    });
                    
                    // Lists
                    const lists = tooltip.querySelectorAll('ul, ol');
                    lists.forEach(function(list) {
                        list.style.margin = '4px 0';
                        list.style.paddingLeft = '20px';
                    });
                    
                    const listItems = tooltip.querySelectorAll('li');
                    listItems.forEach(function(li) {
                        li.style.marginBottom = '2px';
                    });
                };
                
                // Apply styles after a short delay to ensure DOM is ready
                setTimeout(styleMarkdown, 0);
            }
        }
        
        // Show tooltip
        el._showTooltip = function(event) {
            // Clear any existing timeout
            if (el._tooltipTimeout) {
                clearTimeout(el._tooltipTimeout);
            }
            
            // Set timeout for delayed show
            el._tooltipTimeout = setTimeout(function() {
                // Load content first (from URL or cache)
                loadTooltipContent(function(content) {
                    // Create tooltip if it doesn't exist
                    if (!el._tooltipElement) {
                        el._tooltipElement = createTooltip(content);
                    } else {
                        // Update existing tooltip with loaded content
                        updateTooltipContent(el._tooltipElement, content);
                    }
                    
                    const targetRect = el.getBoundingClientRect();
                    
                    // Position tooltip
                    positionTooltip(el._tooltipElement, targetRect);
                    
                    // Show tooltip with fade-in
                    requestAnimationFrame(function() {
                        if (el._tooltipElement) {
                            el._tooltipElement.style.opacity = '1';
                        }
                    });
                });
            }, config.delay);
        };
        
        // Hide tooltip
        el._hideTooltip = function() {
            // Clear show timeout
            if (el._tooltipTimeout) {
                clearTimeout(el._tooltipTimeout);
                el._tooltipTimeout = null;
            }
            
            // Hide tooltip with fade-out
            if (el._tooltipElement) {
                el._tooltipElement.style.opacity = '0';
                
                // Remove after animation
                setTimeout(function() {
                    if (el._tooltipElement && el._tooltipElement.parentNode) {
                        el._tooltipElement.parentNode.removeChild(el._tooltipElement);
                        el._tooltipElement = null;
                    }
                }, 200);
            }
        };
        
        // Update position on mouse move
        el._updatePosition = function() {
            if (el._tooltipElement && el._tooltipElement.style.opacity === '1') {
                const targetRect = el.getBoundingClientRect();
                positionTooltip(el._tooltipElement, targetRect);
            }
        };
        
        // Add event listeners
        el.addEventListener('mouseenter', el._showTooltip);
        el.addEventListener('mouseleave', el._hideTooltip);
        el.addEventListener('mousemove', el._updatePosition);
    },
    
    update: function(el, binding) {
        // Update tooltip content if it changed
        let newText = '';
        let newUrl = null;
        let newMarkdown = false;
        let newIsUrl = binding.modifiers.url || false;
        
        if (typeof binding.value === 'string') {
            if (newIsUrl) {
                newUrl = binding.value;
            } else {
                newText = binding.value;
                newMarkdown = /[*_`#\[\]]/g.test(binding.value);
            }
        } else if (typeof binding.value === 'object') {
            if (binding.value.url) {
                newUrl = binding.value.url;
                newIsUrl = true;
            }
            newText = binding.value.text || '';
            newMarkdown = binding.value.markdown !== undefined ? binding.value.markdown : /[*_`#\[\]]/g.test(newText);
        }
        
        if (el._tooltipConfig) {
            const configChanged = (
                (newUrl && newUrl !== el._tooltipConfig.url) ||
                (newText && newText !== el._tooltipConfig.text) ||
                newMarkdown !== el._tooltipConfig.markdown
            );
            
            if (configChanged) {
                // Clear cache if URL changed
                if (newUrl && newUrl !== el._tooltipConfig.url) {
                    el._tooltipContentLoaded = false;
                    el._tooltipContentCache = null;
                }
                
                // Update config
                el._tooltipConfig.text = newText;
                el._tooltipConfig.url = newUrl;
                el._tooltipConfig.markdown = newMarkdown;
                el._tooltipConfig.isUrl = newIsUrl;
                
                // Update tooltip element if it exists
                if (el._tooltipElement) {
                    if (newIsUrl && newUrl) {
                        // Fetch new content from URL
                        AjaxUtils.ajax_get({
                            url: newUrl,
                            success: function(data) {
                                let content = '';
                                
                                if (typeof data === 'string') {
                                    content = data;
                                } else if (data && data.content) {
                                    content = data.content;
                                } else if (data && data.text) {
                                    content = data.text;
                                } else if (data && data.data) {
                                    content = data.data;
                                }
                                
                                el._tooltipContentCache = content;
                                el._tooltipContentLoaded = true;
                                
                                if (newMarkdown) {
                                    el._tooltipElement.innerHTML = markdownConverter.makeHtml(content);
                                } else {
                                    el._tooltipElement.textContent = content;
                                }
                            },
                            error: function(error) {
                                console.error('Failed to update tooltip content from URL:', newUrl, error);
                            },
                            opts: {
                                dataType: 'json',
                                flash_form_errors: false,
                                show_success_flash: false,
                                flash_404_errors: false
                            }
                        });
                    } else {
                        // Update with text content directly
                        if (newMarkdown) {
                            el._tooltipElement.innerHTML = markdownConverter.makeHtml(newText);
                        } else {
                            el._tooltipElement.textContent = newText;
                        }
                    }
                }
            }
        }
    },
    
    unbind: function(el) {
        // Clean up
        if (el._hideTooltip) {
            el._hideTooltip();
        }
        
        // Remove event listeners
        if (el._showTooltip) {
            el.removeEventListener('mouseenter', el._showTooltip);
        }
        if (el._hideTooltip) {
            el.removeEventListener('mouseleave', el._hideTooltip);
        }
        if (el._updatePosition) {
            el.removeEventListener('mousemove', el._updatePosition);
        }
        
        // Clean up references
        delete el._tooltipConfig;
        delete el._tooltipElement;
        delete el._tooltipTimeout;
        delete el._showTooltip;
        delete el._hideTooltip;
        delete el._updatePosition;
    }
};

// Export the directive (registered globally in globals.js)
export default TooltipDirective;
